import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import {
    api,
    FeedFilters,
    LinkedInAssignment,
    LinkedInPaper,
    LinkedInPost,
    LinkedInRepo,
    LinkedInFacets,
} from '../../services/api'
import { Spinner, LoadingContainer, EmptyState, Button } from '../../components/shared/StyledComponents'
import { FeedTabs, FeedTab } from './components/FeedTabs'
import { RepoMetrics } from './components/RepoMetrics'
import { ClustersView } from './components/ClustersView'
import { ProjectsView } from './components/ProjectsView'
import { PipelineHealthBar } from './components/PipelineHealthBar'
import { FeedFilterBar, FeedEntity } from './components/FeedFilterBar'
import { PostCard } from './components/PostCard'
import { PaperCatalogCard, RepoCatalogCard } from './components/CatalogCards'
import { FeedPagination } from './components/FeedPagination'
import { AssignmentsTable } from './components/AssignmentsTable'
import { AssignTaskModal, AssignTarget } from './components/AssignTaskModal'
import { SotaDetailModal, SotaDetailTarget } from './components/SotaDetailModal'
import { SotaExportMenu } from './components/SotaExportMenu'
import {
    PageTitle,
    PageSubtitle,
    CardGrid,
    Alert,
} from './components/linkedinStyles'

/**
 * SOTA — one list, one entry per piece of content.
 *
 * The page used to split the same material across three feeds (repos, papers,
 * publications) that a reader had to join in their head. There is now a single
 * list of what the bot ingested, each entry badged with what it carries, and the
 * repo/paper detail — README, commits, history, notes, assignments — lives in
 * the modal that opens on click. Clusteres, Métricas and Asignaciones stay:
 * they answer questions the list does not.
 *
 * The list can also be pointed at repos or at papers, from the filter bar. That
 * is not the old tabs under another name — there is still one list, one filter
 * bar and one detail modal — it is the answer to the two questions a list of
 * posts genuinely cannot answer: finding a repo by owner, name, description,
 * language or movement, and reaching the papers the repo agent extracted from a
 * repo's README, which no post mentions and the publication list therefore
 * cannot show at all.
 */

const PAGE_SIZE = 12

const DEFAULT_FILTERS: FeedFilters = {
    sort: 'recent',
    limit: PAGE_SIZE,
    offset: 0,
}

/** True when the reader has narrowed the list — decides which empty state to show. */
const hasActiveFilters = (f: FeedFilters): boolean =>
    !!(f.search || f.category || f.tag || f.date_from || f.date_to || f.language || f.source) ||
    f.has_repo !== undefined ||
    f.has_paper !== undefined ||
    f.has_post !== undefined ||
    f.trending !== undefined ||
    f.min_stars !== undefined ||
    f.include_inactive !== undefined

/** What each entity is called in the sentences the page writes. */
const ENTITY_NOUN: Record<FeedEntity, { singular: string; plural: string }> = {
    posts: { singular: 'contenido', plural: 'contenido' },
    repos: { singular: 'repositorio', plural: 'repositorios' },
    papers: { singular: 'paper', plural: 'papers' },
}

/** How the list on screen is named in the export menu. */
const ENTITY_EXPORT_LABEL: Record<FeedEntity, string> = {
    posts: 'Publicaciones',
    repos: 'Repositorios',
    papers: 'Papers',
}

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`

/** The title yields width before the export button wraps under it. */
const TitleBlock = styled.div`
  flex: 1 1 20rem;
  min-width: 0;
`

export const LinkedInFeedPage: React.FC = () => {
    const { user } = useAuth()
    const isAdmin = user?.role?.toLowerCase() === 'admin'

    const [tab, setTab] = useState<FeedTab>('content')
    const [entity, setEntity] = useState<FeedEntity>('posts')
    const [filters, setFilters] = useState<FeedFilters>(DEFAULT_FILTERS)
    const [facets, setFacets] = useState<LinkedInFacets | null>(null)

    const [posts, setPosts] = useState<LinkedInPost[]>([])
    const [repos, setRepos] = useState<LinkedInRepo[]>([])
    const [papers, setPapers] = useState<LinkedInPaper[]>([])
    const [total, setTotal] = useState(0)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Assignment modal state (started from the detail modal, and from the
    // Asignaciones tab when editing an existing one).
    const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null)

    // Detail modal state (opened by clicking a card). `detailReloadKey` is bumped
    // when something the open modal renders changes underneath it.
    const [detailTarget, setDetailTarget] = useState<SotaDetailTarget | null>(null)
    const [detailReloadKey, setDetailReloadKey] = useState(0)

    // Asignaciones tab state (admin only)
    const [assignments, setAssignments] = useState<LinkedInAssignment[]>([])
    const [editing, setEditing] = useState<LinkedInAssignment | null>(null)
    const [assignmentAlert, setAssignmentAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null)

    // Load facets once.
    useEffect(() => {
        api.linkedin.getFacets()
            .then((res) => setFacets(res.data))
            .catch(() => setFacets(null))
    }, [])

    const loadFeed = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            if (entity === 'repos') {
                const res = await api.linkedin.getRepos(filters)
                setRepos(res.data.items)
                setTotal(res.data.total)
            } else if (entity === 'papers') {
                const res = await api.linkedin.getPapers(filters)
                setPapers(res.data.items)
                setTotal(res.data.total)
            } else {
                // The badges (owner/name, stars, venue, provenance) come back with
                // the row: `has_repo_link` only says *whether* there is one, and
                // asking the detail endpoint per entry cost a round trip each.
                const res = await api.linkedin.getPosts(filters)
                setPosts(res.data.items)
                setTotal(res.data.total)
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Error al cargar el contenido.')
        } finally {
            setLoading(false)
        }
    }, [filters, entity])

    const loadAssignments = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api.linkedin.getAssignments()
            setAssignments(Array.isArray(res.data) ? res.data : [])
        } catch (err: any) {
            setAssignmentAlert({ type: 'error', message: err?.response?.data?.detail || 'Error al cargar las asignaciones.' })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (tab === 'assignments') {
            loadAssignments()
        } else if (tab === 'content') {
            // Clusteres and Métricas own their fetching; the feed query does not
            // apply to them.
            loadFeed()
        }
    }, [tab, loadFeed, loadAssignments])

    // Switching what is listed resets the filters: the three lists share a filter
    // bar but not a vocabulary, and carrying `min_stars` into a paper query (where
    // nothing applies it) would silently show an unfiltered list under a filter
    // the reader can see is set.
    const handleEntityChange = (next: FeedEntity) => {
        if (next === entity) return
        setEntity(next)
        setFilters({ ...DEFAULT_FILTERS })
    }

    const handleTabChange = (next: FeedTab) => {
        if (next === tab) return
        // Guard: non-admins can never reach the Asignaciones tab.
        if (next === 'assignments' && !isAdmin) return
        setTab(next)
        setAssignmentAlert(null)
    }

    // Patch filters (resets offset to 0 for everything except an explicit offset change).
    const handleFilterChange = (patch: Partial<FeedFilters>) => {
        setFilters((prev) => ({
            ...prev,
            ...patch,
            offset: 'offset' in patch ? (patch.offset ?? 0) : 0,
        }))
    }

    const handleClear = () => {
        setFilters({ ...DEFAULT_FILTERS })
    }

    const handlePageChange = (offset: number) => {
        setFilters((prev) => ({ ...prev, offset }))
    }

    const handleCloseAssignment = async (assignment: LinkedInAssignment) => {
        if (!confirm(`¿Cerrar la asignación de "${assignment.user_display_name || `usuario #${assignment.user_id}`}"?`)) {
            return
        }
        try {
            await api.linkedin.closeAssignment(assignment.id)
            setAssignmentAlert({ type: 'success', message: 'Asignación cerrada correctamente.' })
            loadAssignments()
        } catch (err: any) {
            setAssignmentAlert({ type: 'error', message: err?.response?.data?.detail || 'Error al cerrar la asignación.' })
        }
    }

    const renderBody = () => {
        // Checked before the spinner: this view fetches on its own, so the feed's
        // `loading` flag says nothing about it and would leave it stuck spinning.
        if (tab === 'clusters') {
            return <ClustersView onOpenPost={(postId) => setDetailTarget({ type: 'post', id: postId })} />
        }

        if (tab === 'projects') {
            return (
                <ProjectsView
                    isAdmin={isAdmin}
                    reloadKey={detailReloadKey}
                    onOpenItem={(type, id) => setDetailTarget({ type, id })}
                    onAssign={isAdmin ? (t) => setAssignTarget(t) : undefined}
                />
            )
        }

        // Same reason as clusters: it fetches its own data, so the feed's
        // `loading` flag would leave it spinning forever.
        if (tab === 'metrics') {
            return <RepoMetrics />
        }

        if (loading) {
            return (
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            )
        }

        if (tab === 'assignments') {
            return (
                <>
                    {assignmentAlert && <Alert $type={assignmentAlert.type}>{assignmentAlert.message}</Alert>}
                    {assignments.length === 0 ? (
                        <EmptyState>
                            <p>No hay asignaciones todavía.</p>
                            <p style={{ marginTop: '0.5rem' }}>
                                Abre una publicación en Contenido y pulsa «Asignar a interno» sobre su
                                repositorio o su paper.
                            </p>
                            <Button
                                variant="primary"
                                style={{ marginTop: '1rem' }}
                                onClick={() => handleTabChange('content')}
                            >
                                Ir a Contenido
                            </Button>
                        </EmptyState>
                    ) : (
                        <AssignmentsTable
                            assignments={assignments}
                            onEdit={(a) => setEditing(a)}
                            onClose={handleCloseAssignment}
                        />
                    )}
                </>
            )
        }

        if (error) {
            return <Alert $type="error">{error}</Alert>
        }

        const noun = ENTITY_NOUN[entity]
        const listed = entity === 'repos' ? repos.length : entity === 'papers' ? papers.length : posts.length
        if (listed === 0) {
            // Two different problems that used to share one sentence: nothing has
            // been ingested yet, or the filters exclude everything.
            return hasActiveFilters(filters) ? (
                <EmptyState>
                    <p>Ningún {noun.singular} coincide con los filtros.</p>
                    <Button variant="secondary" style={{ marginTop: '1rem' }} onClick={handleClear}>
                        Limpiar filtros
                    </Button>
                </EmptyState>
            ) : (
                <EmptyState>
                    <p>Todavía no hay {noun.plural}.</p>
                    <p style={{ marginTop: '0.5rem' }}>
                        El bot lee los mensajes recibidos una vez al día; lo que llegue aparecerá aquí.
                    </p>
                </EmptyState>
            )
        }

        return (
            <>
                <CardGrid>
                    {entity === 'posts' &&
                        posts.map((p) => (
                            <PostCard
                                key={p.id}
                                post={p}
                                onOpenDetail={(type, id) => setDetailTarget({ type, id })}
                            />
                        ))}
                    {entity === 'repos' &&
                        repos.map((r) => (
                            <RepoCatalogCard
                                key={r.id}
                                repo={r}
                                onOpen={() => setDetailTarget({ type: 'repo', id: r.id })}
                            />
                        ))}
                    {entity === 'papers' &&
                        papers.map((pa) => (
                            <PaperCatalogCard
                                key={pa.id}
                                paper={pa}
                                onOpen={() => setDetailTarget({ type: 'paper', id: pa.id })}
                            />
                        ))}
                </CardGrid>
                <FeedPagination total={total} limit={PAGE_SIZE} offset={filters.offset || 0} onChange={handlePageChange} />
            </>
        )
    }

    return (
        <div>
            <HeaderRow>
                <TitleBlock>
                    <PageTitle>SOTA · Inteligencia de contenidos</PageTitle>
                    <PageSubtitle>
                        Todo lo que el bot ha recogido, una entrada por contenido: cada una indica si trae
                        repositorio, paper o ninguno de los dos. Pulsa para ver el detalle.
                    </PageSubtitle>
                </TitleBlock>
                <SotaExportMenu
                    isAdmin={isAdmin}
                    currentList={
                        tab === 'content'
                            ? {
                                  entity,
                                  filters,
                                  label: ENTITY_EXPORT_LABEL[entity],
                                  filtered: hasActiveFilters(filters),
                              }
                            : undefined
                    }
                />
            </HeaderRow>

            <PipelineHealthBar />

            <div style={{ marginBottom: '1rem' }}>
                <FeedTabs active={tab} onChange={handleTabChange} isAdmin={isAdmin} />
            </div>

            {tab === 'content' && (
                <FeedFilterBar
                    filters={filters}
                    entity={entity}
                    facets={facets}
                    onEntityChange={handleEntityChange}
                    onChange={handleFilterChange}
                    onClear={handleClear}
                />
            )}

            {renderBody()}

            <SotaDetailModal
                target={detailTarget}
                reloadKey={detailReloadKey}
                onClose={() => setDetailTarget(null)}
                onAssign={isAdmin ? (t) => setAssignTarget(t) : undefined}
            />

            <AssignTaskModal
                isOpen={!!assignTarget}
                onClose={() => setAssignTarget(null)}
                target={assignTarget || undefined}
                onSaved={() => {
                    // AssignTaskModal confirms and closes itself. The list does not
                    // reflect assignment state, but the detail modal underneath it
                    // renders `assignments` and has just gone stale, so it is asked
                    // to refetch; the Asignaciones tab fetches fresh when opened.
                    setDetailReloadKey((k) => k + 1)
                }}
            />

            <AssignTaskModal
                isOpen={!!editing}
                onClose={() => setEditing(null)}
                assignment={editing || undefined}
                onSaved={() => {
                    setAssignmentAlert({ type: 'success', message: 'Asignación actualizada correctamente.' })
                    loadAssignments()
                }}
            />
        </div>
    )
}

export default LinkedInFeedPage
